/**
 * Quote Response Validator - Domain Layer
 *
 * Pure business validation logic for quote responses
 * Uses shared Zod schema from @semo/api-contracts for consistency with Backend
 *
 * @module quoteResponseValidator
 */

import { quoteResponsesControllerCreateBody } from "@semo/api-contracts";
import {
  calculateQuoteAmounts,
  DEFAULT_VAT_MODE,
} from "@semo/shared-utils";
import type {
  QuoteItem,
  QuoteVatMode,
} from "@semo/shared-utils";

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Validate quote response data before submission
 * @param data - Quote response data to validate
 * @returns Validation result with errors map
 */
export const validateQuoteResponse = (data: {
  items: QuoteItem[];
  notes?: string;
  /** 단가 입력 축 — 생략 시 부가세 별도(공급가액) */
  vatMode?: QuoteVatMode;
}): ValidationResult => {
  const errors: Record<string, string> = {};

  // Calculate totals — 화면·전송과 같은 정본(공급가액 기준 사사오입 / 포함가는 역산)
  const { vatAmount: taxAmount, totalAmount } = calculateQuoteAmounts(
    data.items,
    data.vatMode ?? DEFAULT_VAT_MODE,
  );

  // 1. Map UI state to DTO structure for Zod validation
  // Note: We construct a partial DTO just for validation purposes
  const dtoForValidation = {
    // Basic fields
    description: data.notes || undefined,
    // Calculate required fields to satisfy schema structure
    totalAmount,
    taxAmount,
    // Map items
    items: data.items.map((item) => ({
      name: item.itemName,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      unit: "EA", // Default unit for validation context
      amount: (item.quantity || 0) * (item.unitPrice || 0),
    })),
  };

  // 2. Run Zod Validation
  const result = quoteResponsesControllerCreateBody.safeParse(dtoForValidation);

  if (!result.success) {
    result.error.issues.forEach((issue) => {
      const path = issue.path;
      const message = issue.message;

      // Map Zod paths to UI field names
      // Zod: items[0].name -> UI: items.0.itemName
      // Zod: items[0].unitPrice -> UI: items.0.unitPrice
      // Zod: description -> UI: notes

      if (path[0] === "items" && typeof path[1] === "number") {
        const index = path[1];
        const field = path[2];

        if (field === "name") {
          errors[`items.${index}.itemName`] = "품목명을 입력해주세요";
        } else if (field === "quantity") {
          errors[`items.${index}.quantity`] =
            message === "Required" ? "수량을 입력해주세요" : message;
        } else if (field === "unitPrice") {
          errors[`items.${index}.unitPrice`] =
            message === "Required" ? "단가(공급가액)를 입력해주세요" : message;
        }
      } else if (path[0] === "description") {
        errors.notes = message;
      }
    });
  }

  // 3. Custom Validations (Business Rules not in Zod or stricter than Zod)
  // These are applied on top to ensure specific UX requirements
  data.items.forEach((item, index) => {
    // Required fields (explicit check because Zod mapping might be loose)
    if (!item.itemName || item.itemName.trim().length === 0) {
      errors[`items.${index}.itemName`] = "품목명을 입력해주세요";
    } else if (item.itemName.length > 100) {
      errors[`items.${index}.itemName`] = "품목명은 100자 이내로 입력해주세요";
    }

    if (item.quantity === undefined || item.quantity === null) {
      errors[`items.${index}.quantity`] = "수량을 입력해주세요";
    } else if (item.quantity <= 0) {
      errors[`items.${index}.quantity`] = "수량은 0보다 커야 합니다";
    } else if (!Number.isInteger(item.quantity)) {
      errors[`items.${index}.quantity`] = "수량은 정수여야 합니다";
    }

    if (item.unitPrice === undefined || item.unitPrice === null) {
      errors[`items.${index}.unitPrice`] = "단가(공급가액)를 입력해주세요";
    } else if (item.unitPrice <= 0) {
      errors[`items.${index}.unitPrice`] = "단가(공급가액)는 0보다 커야 합니다";
    } else if (item.unitPrice > 999999999) {
      errors[`items.${index}.unitPrice`] =
        "단가(공급가액)는 999,999,999원 이하여야 합니다";
    }
  });

  if (data.notes && data.notes.length > 1000) {
    errors.notes = "특이사항은 1000자 이내로 입력해주세요";
  }

  // Ensure at least one item exists (Zod might allow empty array if optional)
  if (data.items.length === 0) {
    errors.items = "최소 1개 이상의 항목이 필요합니다";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Validate single quote item
 * @param item - Quote item to validate
 * @param index - Item index for error messages
 * @returns Validation result
 */
export const validateQuoteItem = (
  item: QuoteItem,
  _index = 0,
): ValidationResult => {
  return validateQuoteResponse({ items: [item] });
};

/**
 * Check if item name is valid
 * @param itemName - Item name to validate
 * @returns True if valid
 */
export const isValidItemName = (itemName: string): boolean => {
  return !!itemName && itemName.trim().length > 0 && itemName.length <= 100;
};

/**
 * Check if quantity is valid
 * @param quantity - Quantity to validate
 * @returns True if valid
 */
export const isValidQuantity = (quantity: number): boolean => {
  return quantity > 0 && Number.isInteger(quantity);
};

/**
 * Check if unit price is valid
 * @param unitPrice - Unit price to validate
 * @returns True if valid
 */
export const isValidUnitPrice = (unitPrice: number): boolean => {
  return unitPrice > 0 && unitPrice <= 999999999;
};
